/**
 * 소개 페이지·가이드 → 도구(/app)로 글자 넘기기.
 * 글자는 주소의 # 뒤(해시)에 싣는다. 해시는 HTTP 요청·Referer 에 실리지 않으므로 서버로 가지 않는다.
 * 도구는 시작할 때 해시를 읽어 입력 칸을 채우고, 곧바로 주소에서 해시를 지운다(src/client/app.ts).
 *   /app#t=%ED%99%8D%EA%B8%B8%EB%8F%99&h=15
 *   /app#t=…&h=15&f=cap   (가이드 링크: 원단 프리셋까지 골라 연다. 프리셋 id 가 아니면 무시)
 */
import { FABRIC_ORDER, type FabricId } from "../engine/params";

/** 도구 입력 칸과 같은 한도: 최대 4줄, 줄마다 24자 */
export const HANDOFF_MAX_LINES = 4;
export const HANDOFF_MAX_CHARS = 24;
/** 도구의 글자 높이 범위(mm)와 눈금 */
export const HANDOFF_MIN_H = 5;
export const HANDOFF_MAX_H = 50;

export interface Handoff {
  /** 넘겨받은 글자. 없으면 null (도구 기본 글자 유지) */
  text: string | null;
  /** 글자 높이 mm. 없거나 범위 밖이면 null (도구 기본 높이 유지) */
  heightMm: number | null;
  /** 원단 프리셋 (가이드 링크). 없거나 모르는 값이면 이 속성이 없다 (도구 기본 원단 유지) */
  fabric?: FabricId;
}

function cleanFabric(v: unknown): FabricId | null {
  return typeof v === "string" && (FABRIC_ORDER as string[]).includes(v) ? (v as FabricId) : null;
}

/** 줄 수·줄 길이를 도구 한도로 자르고, 제어 문자를 뺀다 */
export function cleanText(s: string): string {
  const lines = s
    .replace(/\r\n?/g, "\n")
    .replace(/[\u0000-\u0009\u000B-\u001F\u007F]/g, "")
    .split("\n")
    .map((l) => [...l].slice(0, HANDOFF_MAX_CHARS).join("").trimEnd())
    .filter((l) => l.trim() !== "")
    .slice(0, HANDOFF_MAX_LINES);
  return lines.join("\n").trim();
}

function cleanHeight(v: unknown): number | null {
  const n = Number(v);
  if (!Number.isFinite(n) || n < HANDOFF_MIN_H || n > HANDOFF_MAX_H) return null;
  return Math.round(n * 2) / 2;
}

/** 소개 페이지 입력·가이드 링크 → 도구 주소. 글자가 비었고 높이·원단도 없으면 그냥 /app */
export function handoffUrl(text: string, heightMm?: number | null, fabric?: FabricId | null): string {
  const t = cleanText(text);
  const h = heightMm == null ? null : cleanHeight(heightMm);
  const f = cleanFabric(fabric);
  const parts: string[] = [];
  if (t) parts.push(`t=${encodeURIComponent(t)}`);
  if (h != null) parts.push(`h=${h}`);
  if (f) parts.push(`f=${f}`);
  return parts.length ? `/app#${parts.join("&")}` : "/app";
}

/** 도구 시작 때 location.hash 읽기. t·h·f 가 모두 없으면(#sheet 같은 앵커) null */
export function parseHandoff(hash: string): Handoff | null {
  if (!hash || hash[0] !== "#") return null;
  const p = new URLSearchParams(hash.slice(1));
  if (!p.has("t") && !p.has("h") && !p.has("f")) return null;
  const t = p.has("t") ? cleanText(p.get("t") ?? "") : "";
  const text = t || null;
  const heightMm = p.has("h") ? cleanHeight(p.get("h")) : null;
  const fabric = cleanFabric(p.get("f"));
  if (text == null && heightMm == null && fabric == null) return null;
  return fabric ? { text, heightMm, fabric } : { text, heightMm };
}
